import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationDetailComponent } from '@components/notification-detail/notification-detail.component';
import { GlobalNotification, Notification } from '@models/notifications/notification.model';
import { FilterType } from '@modules/shared/components/type-filter/type-filter.component';
import { TypeFilterDialogComponent } from '@modules/shared/dialogs/type-filter-dialog/type-filter-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DisplayService } from '@services/display.service';
import { NotificationService } from '@services/notification.service';
import { BehaviorSubject, Observable, take } from 'rxjs';

@Component({
  selector: 'gc-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false,
})
export class NotificationsComponent implements OnInit {
  @ViewChild(MatAccordion, { static: true }) accordion: MatAccordion;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  sort: MatSort;
  searchControl: FormControl = new FormControl();
  // Underlying notifications data.
  _notifications: Notification[] = [];
  // Datasource proxy for notifications. Used for sorting and filtering.
  _notificationsDataSource: MatTableDataSource<Notification> = new MatTableDataSource();
  // Notifications observable to be used by the UI.
  notifications$: Observable<Notification[]> = this._notificationsDataSource.connect();
  isMobile$: BehaviorSubject<boolean>;
  searchMode = false;
  typeFilters: FilterType[] = [
    {
      title: this.translateService.instant('questionnaires'),
      type: 'questionnaire',
      icon: Notification.types.questionnaire.icon,
      selected: false,
    },
    {
      title: this.translateService.instant('consent_forms'),
      type: 'consent-form',
      icon: Notification.types['consent-form'].icon,
      selected: false,
    },
    {
      title: this.translateService.instant('documents'),
      type: 'document',
      icon: Notification.types.document.icon,
      selected: false,
    },
    {
      title: this.translateService.instant('payments'),
      type: 'payment',
      icon: Notification.types.payment.icon,
      selected: false,
    },
    {
      title: this.translateService.instant('bookings'),
      type: 'booking',
      icon: Notification.types.booking.icon,
      selected: false,
    },
    {
      title: this.translateService.instant('demographic_request'),
      type: 'demographic-request',
      icon: Notification.types['demographic-request'].icon,
      selected: false,
    },
    {
      title: this.translateService.instant('medical_history'),
      type: 'medical-history',
      icon: Notification.types['medical-history'].icon,
      selected: false,
    },
    {
      title: this.translateService.instant('system_notifications'),
      type: 'global-notification',
      icon: Notification.types['global-notification'].icon,
      selected: false,
    },
  ];
  noNotifications = false;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly notification: NotificationService,
    public display: DisplayService,
    public dialog: MatDialog,
    private readonly translateService: TranslateService,
  ) {}

  @ViewChild(MatSort) set content(content: MatSort) {
    if (content) {
      this.sort = content;
      this._notificationsDataSource.sort = this.sort;
      this.ref.detectChanges();
    }
  }

  ngOnInit() {
    this.isMobile$ = this.display.isMobile$;
    this._notificationsDataSource.paginator = this.paginator;
    this._notificationsDataSource.filterPredicate = (data, filter) =>
      ['type', 'sentDate', 'description'].some((f) => data[f] && data[f].toLowerCase().indexOf(filter) !== -1);
    this.searchControl.valueChanges.subscribe((searchTerm) => {
      this._notificationsDataSource.filter = searchTerm.trim().toLowerCase();
      this._notificationsDataSource.paginator.firstPage();
    });
    this.updateNotifications();
  }

  onFilter(data: Notification[]) {
    this._notificationsDataSource.data = data;
    this.noNotifications = !data.length;
  }

  updateNotifications() {
    this.notification.fetchNotifications$('all').subscribe((result) => {
      this._notifications = this._notificationsDataSource.data = result;
      this.noNotifications = !result.length;
    });
  }

  onNotificationActionClick(event: Event, notification: Notification) {
    if (notification.type === 'global-notification') {
      this.openNotificationDetail(notification.globalNotification);
      this.notification
        .markAsRead$(notification)
        .pipe(take(1))
        .subscribe(() => {
          this.updateNotifications();
        });
    } else {
      this.notification.notificationAction(notification);
    }
    event.stopPropagation();
  }

  openNotificationDetail(notification: GlobalNotification) {
    this.dialog.open(NotificationDetailComponent, {
      data: notification,
      width: '400px',
      maxWidth: '95vw',
    });
  }

  markAllAsRead(): void {
    this.notification.markAllAsRead$().subscribe(() => {
      this._notifications.forEach((n) => {
        if (!n.readDate) {
          n.readDate = new Date().toISOString();
        }
      });
      this._notificationsDataSource.data = [...this._notifications];
      this.noNotifications = !this._notifications.length;
      this.notification.amountOfUnreadNotifications$.next(0);
      this.ref.detectChanges();
    });
  }

  openMobileFilterPopup(): void {
    const dialogRef = this.dialog.open(TypeFilterDialogComponent, {
      position: { bottom: '0' },
      width: '100%',
      maxWidth: '100%',
      data: { types: this.typeFilters, data: this._notifications },
    });

    dialogRef.componentInstance.filterCallback.subscribe((filteredData: Notification[]) => {
      this._notificationsDataSource.data = filteredData;
      this.noNotifications = filteredData.length === 0;
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ref.detectChanges();
    });
  }
}
